import { useEffect, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import api from '@/lib/api'

interface UpdateValueDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: number
  currentValue: number
  onUpdated: (value: number) => void
}

export function UpdateValueDialog({
  open,
  onOpenChange,
  accountId,
  currentValue,
  onUpdated,
}: UpdateValueDialogProps) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setValue(currentValue ? String(currentValue) : '')
      setError(null)
      setIsSaving(false)
    }
  }, [open, currentValue])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    const parsed = Number(value.trim())
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError('Enter a valid value.')
      return
    }

    setIsSaving(true)
    try {
      const response = await api.put(`/manual_accounts/${accountId}`, {
        manual_total_value: parsed,
      })
      const updated = response.data.account?.manual_total_value
      onUpdated(typeof updated === 'number' ? updated : parsed)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to update value:', err)
      setError('Unable to update value. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Total Value</DialogTitle>
          <DialogDescription>
            Cheque tracks the change in your net worth automatically.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="update-value">Total value</Label>
            <Input
              id="update-value"
              type="number"
              min="0"
              step="any"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              autoFocus
            />
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
